import { isPlatformBrowser } from '@angular/common';
import {
  inject,
  Injectable,
  PLATFORM_ID,
  RendererFactory2,
  DOCUMENT,
} from '@angular/core';
import { ActivatedRoute } from '@freelancer/activated-route';
import { Auth } from '@freelancer/auth';
import { LocalStorage } from '@freelancer/local-storage';
import { UI_CONFIG } from '@freelancer/ui/ui.config';
import type { Observable } from 'rxjs';
import { combineLatest, fromEvent, of } from 'rxjs';
import { map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import type { Theme, ThemeSetting } from './theme.types';
import { ShellConfig } from '../../app/app-shell/shell-config.service';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private document = inject(DOCUMENT);
  private localStorage = inject(LocalStorage);
  private platformId = inject(PLATFORM_ID);
  private renderer = inject(RendererFactory2).createRenderer(null, null);
  private activatedRoute = inject(ActivatedRoute);
  private auth = inject(Auth);
  private shellConfig = inject(ShellConfig);
  private uiConfig = inject(UI_CONFIG);

  private readonly DARK_MODE_MEDIA_QUERY = '(prefers-color-scheme: dark)';

  /**
   * themeSetting is the actual setting that the user has chosen (or been defaulted to).
   * This can be: 'light' | 'dark' | 'system'. This setting is stored in local storage.
   */
  private readonly themeSetting$: Observable<ThemeSetting>;
  /**
   * Theme is the actual theme being used in the app. This is either 'light' or 'dark'.
   * If the themeSetting is 'system', then the theme will be 'light' or 'dark' based on the user's OS settings.
   */
  private readonly theme$: Observable<Theme>;

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      // Forced-dark routes skip auth/storage so they never paint light first.
      // Every other page still resolves auth and local storage in parallel.
      this.themeSetting$ = this.shellConfig
        .getConfig(this.activatedRoute.activatedRoute)
        .pipe(
          switchMap((config): Observable<ThemeSetting> => {
            // Check for specific themes first (priority over everything)
            if (
              this.uiConfig.theme === 'deloitte' ||
              this.uiConfig.theme === 'freightlancer'
            ) {
              return of('light');
            }

            if (config.forceDarkMode) {
              return of('dark');
            }

            // Logged-out forced-dark routes: auth only. Skip storage so the
            // page isn't held on the light default while an unused preference loads.
            if (config.forceDarkModeWhenLoggedOut) {
              return this.auth.isLoggedIn().pipe(
                switchMap((isLoggedIn): Observable<ThemeSetting> => {
                  if (!isLoggedIn) {
                    return of('dark');
                  }

                  return this.localStorage
                    .get('theme')
                    .pipe(map((theme): ThemeSetting => theme ?? 'light'));
                }),
              );
            }

            return combineLatest([
              this.auth.isLoggedIn(),
              this.localStorage.get('theme'),
            ]).pipe(
              map(
                ([, theme]): ThemeSetting =>
                  // The item is undefined for users who have never set a theme.
                  theme ?? 'light',
              ),
            );
          }),
          shareReplay({ bufferSize: 1, refCount: true }),
        );

      const darkModeMediaQuery = window.matchMedia(this.DARK_MODE_MEDIA_QUERY);

      const systemPrefersDark$ = fromEvent(darkModeMediaQuery, 'change').pipe(
        startWith(undefined),
        map(() => darkModeMediaQuery.matches),
      );

      // The actual theme being used in the app.
      // Derived from the themeSetting and the system theme.
      this.theme$ = combineLatest([
        this.themeSetting$,
        systemPrefersDark$,
      ]).pipe(
        map(([theme, systemPrefersDark]) =>
          theme === 'system' ? (systemPrefersDark ? 'dark' : 'light') : theme,
        ),
        // Shared so the many getTheme() consumers don't each attach their own
        // 'change' listener to the media query.
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    } else {
      // SSR only runs for logged-out sessions, so forced-dark routes have to be
      // rendered dark here too, otherwise they flip light to dark on hydration.
      this.theme$ = this.shellConfig
        .getConfig(this.activatedRoute.activatedRoute)
        .pipe(
          map((config): Theme => {
            if (
              this.uiConfig.theme === 'deloitte' ||
              this.uiConfig.theme === 'freightlancer'
            ) {
              return 'light';
            }

            if (config.forceDarkModeWhenLoggedOut || config.forceDarkMode) {
              return 'dark';
            }

            return 'light';
          }),
          shareReplay({ bufferSize: 1, refCount: true }),
        );
      this.themeSetting$ = this.theme$;
    }
  }

  /**
   * Return the current theme the user has selected.
   * This can be 'light' | 'dark' | 'system'.
   * You are probably looking for getTheme() instead.
   * This is to only be used when we need to know the user's actual setting.
   */
  getThemeSetting(): Observable<ThemeSetting> {
    return this.themeSetting$;
  }

  /**
   * Returns the current theme being set on the app.
   * If the themeSetting is 'system', then the theme will be 'light' or 'dark' based on the user's OS settings.
   * If the themeSetting is 'light' or 'dark', then the theme will be 'light' or 'dark' respectively.
   * Use this in the app code to get the current theme.
   */
  getTheme(): Observable<Theme> {
    return this.theme$;
  }

  async setThemeSetting(theme: ThemeSetting): Promise<void> {
    await this.localStorage.set('theme', theme);
  }

  /**
   * Applies the dark class to the DOM if the theme is dark.
   */
  private applyThemeToDOM(theme: Theme): void {
    const docElement = this.document.documentElement;
    if (theme === 'dark') {
      this.renderer.addClass(docElement, 'dark');
    } else if (docElement.classList.contains('dark')) {
      // Only remove when present: on the server an unconditional removeClass
      // writes an empty class="" onto <html>, changing the rendered tag.
      this.renderer.removeClass(docElement, 'dark');
    }
  }

  applyTheme(theme: Theme): void {
    this.applyThemeToDOM(theme);
  }
}
